/**
 * CommercialCleanersBoard — Iter-143 · FIX 2.
 *
 * Mobile PWA "Commercial Cleaners" tab. Mirrors the Valet HQ command
 * center structure — same board types (live routes, violations,
 * leaderboards, audit logs) — but populated exclusively from
 * /api/cleaners/* endpoints. No Valet data is fetched here.
 *
 * The 9 commercial business types (office, retail, gas_station,
 * school, medical, warehouse, restaurant, mall, church) each get their
 * own service-line board driven by the existing quote-engine data —
 * this component only READS from /api/cleaners/service-lines and
 * /api/cleaners/board/{type}. No quote-engine logic is recreated here.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, Radio, Trophy, Shield, RefreshCw, Sparkles, Bot,
  Boxes, MapPin,
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { INDUSTRIES } from '../../lib/industryData';
import { getFlattenedServices, getCanonicalPackageName } from '../../lib/packageDetails';

const API = process.env.REACT_APP_BACKEND_URL;
const CYAN = '#22d3ee';
const RED = '#f87171';
const GOLD = '#fbbf24';

const LINE_ICON = {
  office: '🏬', retail: '🛍️', gas_station: '⛽',
  school: '🏫', medical: '🏥', warehouse: '🏭',
  restaurant: '🍽️', mall: '🏦', church: '⛪',
  // Iter-149 · Section 9/14-16 — Strategic Enterprise verticals.
  bank: '🏦', stadium: '🏟️', airport: '✈️',
};

export default function CommercialCleanersBoard({
  testId = 'staff-app-cleaners-board',
  boardFilter = null,
}) {
  const [lines, setLines] = useState(null);      // service-line summaries
  const [activeType, setActiveType] = useState(null);
  const [board, setBoard] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [delayed, setDelayed] = useState([]);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(false);
  // FIX 3a — abort in-flight fetches when the user picks a different
  // service-line tile so stale data can never leak into the board.
  const abortRef = useRef(null);

  // Iter-144 · FIX 2 — respect the Enterprise Pivot Filter when present.
  // We force view_as=cleaners so this board never mixes Valet data.
  const scopeQs = useMemo(() => {
    const base = boardFilter?.queryString || '';
    const params = new URLSearchParams(base);
    params.set('view_as', 'cleaners');
    return params.toString();
  }, [boardFilter?.queryString]);

  const loadLines = useCallback(async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/cleaners/service-lines?${scopeQs}`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        const arr = d.service_lines || [];
        setLines(arr);
        if (arr.length && !activeType) setActiveType(arr[0].property_type);
      } else {
        setLines([]);
      }
    } catch { setLines([]); } finally { setBusy(false); }
  }, [activeType, scopeQs]);

  const loadBoard = useCallback(async (type) => {
    if (!type) return;
    // Cancel any in-flight fetches for a previous tile.
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setBusy(true);
    try {
      const [bRes, rRes, dRes, aRes] = await Promise.all([
        fetch(`${API}/api/cleaners/board/${type}?${scopeQs}`, { credentials: 'include', signal: controller.signal }),
        fetch(`${API}/api/cleaners/live-routes?property_type=${type}&${scopeQs}`, { credentials: 'include', signal: controller.signal }),
        fetch(`${API}/api/cleaners/delayed-alerts?property_type=${type}&${scopeQs}`, { credentials: 'include', signal: controller.signal }),
        fetch(`${API}/api/cleaners/gate-code-audit?property_type=${type}&${scopeQs}`, { credentials: 'include', signal: controller.signal }),
      ]);
      if (bRes.ok) setBoard(await bRes.json()); else setBoard(null);
      if (rRes.ok) { const j = await rRes.json(); setRoutes(j.routes || []); } else setRoutes([]);
      if (dRes.ok) { const j = await dRes.json(); setDelayed(j.delayed_routes || j.alerts || []); } else setDelayed([]);
      if (aRes.ok) { const j = await aRes.json(); setAudit(j.rows || j.entries || []); } else setAudit([]);
    } catch (err) {
      if (err?.name === 'AbortError') return; // superseded by newer selection
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
        setBusy(false);
      }
    }
  }, [scopeQs]);

  useEffect(() => { loadLines(); }, [loadLines]);
  useEffect(() => {
    if (activeType) loadBoard(activeType);
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [activeType, loadBoard]);

  const activeLine = useMemo(
    () => (lines || []).find((l) => l.property_type === activeType),
    [lines, activeType],
  );

  return (
    <section
      className="rounded-xl border-2 p-4 bg-zinc-900/70"
      style={{ borderColor: 'rgba(34,211,238,0.35)' }}
      data-testid={testId}
    >
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <h2 className="flex items-center gap-2 text-sm font-black uppercase tracking-[0.2em]" style={{ color: CYAN }}>
          <Sparkles className="h-4 w-4" /> Commercial Cleaners HQ
        </h2>
        <button
          type="button"
          onClick={() => { loadLines(); if (activeType) loadBoard(activeType); }}
          disabled={busy}
          className="min-h-[40px] min-w-[40px] grid place-items-center rounded-full border border-zinc-600 text-zinc-300 hover:text-white"
          data-testid={`${testId}-refresh`}
        >
          <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* 9 service-line tiles — one per commercial business type. */}
      {lines === null ? (
        <p className="text-xs text-zinc-500" data-testid={`${testId}-loading`}>Loading service lines…</p>
      ) : lines.length === 0 ? (
        <p className="text-xs text-zinc-500 py-4 text-center" data-testid={`${testId}-empty`}>
          No Cleaners service lines in your region yet.
        </p>
      ) : (
        <div className="grid grid-cols-3 gap-2 mb-4" data-testid={`${testId}-lines`}>
          {lines.map((l) => {
            const isActive = l.property_type === activeType;
            return (
              <button
                key={l.property_type}
                type="button"
                onClick={() => setActiveType(l.property_type)}
                className={`min-h-[64px] rounded-lg border p-2 text-left transition-colors ${
                  isActive ? 'bg-cyan-500/15 border-cyan-500 text-white' : 'bg-zinc-950/60 border-zinc-800 text-zinc-300'
                }`}
                data-testid={`${testId}-line-${l.property_type}`}
              >
                <div className="flex items-center gap-1">
                  <span>{LINE_ICON[l.property_type] || '🧼'}</span>
                  <span className="text-[10px] font-bold uppercase tracking-widest truncate">
                    {l.necs_name || l.label || l.property_type}
                  </span>
                </div>
                <p className="text-[9px] text-zinc-500 mt-1">
                  {(l.property_count || 0)} sites · {(l.active_routes || 0)} routes
                </p>
              </button>
            );
          })}
        </div>
      )}

      {/* Board detail for the selected line — mirrors Valet HQ structure. */}
      {activeLine && (
        <div className="space-y-3" data-testid={`${testId}-detail`}>
          <div className="rounded-lg bg-zinc-950/70 border border-zinc-800 p-3">
            <p className="text-[9px] uppercase tracking-widest text-zinc-500">Line</p>
            <p className="text-white font-bold" data-testid={`${testId}-detail-name`}>
              {LINE_ICON[activeLine.property_type]} {activeLine.necs_name || activeLine.label}
            </p>
            <div className="grid grid-cols-3 gap-2 mt-3">
              <StatTile label="Active routes" value={activeLine.active_routes ?? 0} accent={CYAN} testId={`${testId}-stat-routes`} />
              <StatTile label="Violations · window" value={activeLine.weekly_violations ?? 0} accent={RED} testId={`${testId}-stat-violations`} />
              <StatTile label="Properties" value={activeLine.property_count ?? 0} accent={GOLD} testId={`${testId}-stat-properties`} />
            </div>
          </div>

          {/* Live routes */}
          <BoardCard
            testId={`${testId}-live-routes`}
            title={`Live crew positions (${routes.length})`}
            icon={<Radio className="h-4 w-4" style={{ color: CYAN }} />}
          >
            {routes.length === 0 ? (
              <p className="text-[11px] text-zinc-500">No active routes in the current window.</p>
            ) : (
              <ul className="space-y-1 text-[11px]">
                {routes.slice(0, 12).map((r, i) => (
                  <li key={i} className="flex justify-between items-center border-b border-zinc-800 py-1">
                    <span className="truncate mr-2">{r.agent_name || r.agent_id || 'Unknown'} · {r.property_name || '—'}</span>
                    <Badge className="bg-zinc-800 text-zinc-200 text-[9px]">{r.status || 'active'}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </BoardCard>

          {/* Violations */}
          <BoardCard
            testId={`${testId}-violations`}
            title={`Violations (${(board?.violations || []).length})`}
            icon={<AlertTriangle className="h-4 w-4" style={{ color: RED }} />}
          >
            {(board?.violations || []).length === 0 ? (
              <p className="text-[11px] text-zinc-500">No open violations in this line.</p>
            ) : (
              <ul className="space-y-1 text-[11px]">
                {board.violations.slice(0, 10).map((v, i) => (
                  <li key={i} className="flex justify-between items-center border-b border-zinc-800 py-1">
                    <span className="truncate mr-2">{v.violation_type || v.type || v.reason || '—'}</span>
                    <span className="text-zinc-500 text-[10px]">{v.property_name || v.property_id}</span>
                  </li>
                ))}
              </ul>
            )}
          </BoardCard>

          {/* Leaderboard */}
          <BoardCard
            testId={`${testId}-leaderboard`}
            title="Leaderboard"
            icon={<Trophy className="h-4 w-4" style={{ color: GOLD }} />}
          >
            {(board?.leaderboard || []).length === 0 ? (
              <p className="text-[11px] text-zinc-500">No rankings yet — populates as crews log service summaries.</p>
            ) : (
              <ol className="space-y-1 text-[11px] list-decimal pl-4">
                {board.leaderboard.slice(0, 10).map((row, i) => (
                  <li key={i} className="flex justify-between border-b border-zinc-800 py-1">
                    <span className="truncate mr-2">{row.name || row.agent_name || row.agent_id}</span>
                    <span className="text-zinc-400">{row.score || row.points || row.completed || 0}</span>
                  </li>
                ))}
              </ol>
            )}
          </BoardCard>

          {/* Delayed alerts */}
          <BoardCard
            testId={`${testId}-delayed`}
            title={`Delayed routes (${delayed.length})`}
            icon={<AlertTriangle className="h-4 w-4" style={{ color: RED }} />}
          >
            {delayed.length === 0 ? (
              <p className="text-[11px] text-zinc-500">All routes on schedule.</p>
            ) : (
              <ul className="space-y-1 text-[11px]">
                {delayed.slice(0, 8).map((d, i) => (
                  <li key={i} className="flex justify-between border-b border-zinc-800 py-1">
                    <span className="truncate mr-2">{d.property_name}</span>
                    <Badge className="bg-red-500/20 text-red-200 border border-red-500 text-[9px]">
                      Escalate → {d.escalation_level || '—'}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </BoardCard>

          {/* Audit log */}
          <BoardCard
            testId={`${testId}-audit`}
            title={`Gate-code audit (${audit.length})`}
            icon={<Shield className="h-4 w-4" style={{ color: CYAN }} />}
          >
            {audit.length === 0 ? (
              <p className="text-[11px] text-zinc-500">No audit entries yet.</p>
            ) : (
              <ul className="space-y-1 text-[11px]">
                {audit.slice(0, 8).map((a, i) => (
                  <li key={i} className="flex justify-between border-b border-zinc-800 py-1">
                    <span className="truncate mr-2 font-mono">
                      {a.property_name || a.property_id?.slice(0, 12) || '—'}
                    </span>
                    <span className="text-zinc-500 text-[10px]">
                      {a.gate_code_masked || '•••'}{a.updated_at ? ` · ${a.updated_at.slice(0, 10)}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </BoardCard>

          {/* AI Accuracy for this line, if present */}
          {board?.ai_accuracy && (
            <div className="rounded-lg bg-zinc-950/70 border border-zinc-800 p-3 flex items-center gap-3">
              <Bot className="h-5 w-5" style={{ color: '#a78bfa' }} />
              <div className="flex-1">
                <p className="text-[9px] uppercase tracking-widest text-zinc-500">AI accuracy</p>
                <p className="text-white font-bold text-sm">
                  {board.ai_accuracy.accuracy_pct != null ? `${board.ai_accuracy.accuracy_pct}%` : '—'}
                </p>
              </div>
            </div>
          )}

          {/* Iter-144 · FIX 1 — Service Packages per property. Mirrors the
              multifamily/Valet PM property view: business name + business
              type label (commercial-specific), contracted service line
              (tier), individual services from the quote engine, region
              (state) + city (blank until field ops assigns it). */}
          <ServicePackagesSection
            testId={`${testId}-packages`}
            properties={board?.properties || []}
            propertyType={activeType}
            propertyLabel={board?.property_label || activeLine?.label}
            necsName={board?.necs_name || activeLine?.necs_name}
          />
        </div>
      )}
    </section>
  );
}

function ServicePackagesSection({ testId, properties, propertyType, propertyLabel, necsName }) {
  // Iter-144 · FIX 1 — commercial-specific label wins over any
  // multifamily fallback. INDUSTRIES ships the CEO-approved commercial
  // labels for all 9 business types.
  const commercialLabel = INDUSTRIES?.[propertyType]?.label || propertyLabel || propertyType;
  const commercialShort = INDUSTRIES?.[propertyType]?.name || necsName;

  return (
    <BoardCard
      testId={testId}
      title={`Service packages · ${properties.length} ${properties.length === 1 ? 'property' : 'properties'}`}
      icon={<Boxes className="h-4 w-4" style={{ color: GOLD }} />}
    >
      <p className="text-[10px] text-zinc-500 mb-2">
        {commercialShort} · {commercialLabel} (commercial line)
      </p>
      {properties.length === 0 ? (
        <p className="text-[11px] text-zinc-500 py-4 text-center">
          No properties assigned to this commercial line yet.
        </p>
      ) : (
        <ul className="space-y-2" data-testid={`${testId}-list`}>
          {properties.slice(0, 20).map((p) => (
            <PropertyServiceCard
              key={p.property_id}
              property={p}
              commercialLabel={commercialLabel}
              testId={`${testId}-property-${p.property_id}`}
            />
          ))}
          {properties.length > 20 && (
            <li className="text-[10px] text-zinc-500 text-center py-2">
              Showing 20 of {properties.length}. Use the Enterprise Pivot Filter to narrow the region.
            </li>
          )}
        </ul>
      )}
    </BoardCard>
  );
}

function PropertyServiceCard({ property, commercialLabel, testId }) {
  const [expanded, setExpanded] = useState(false);
  // Iter-144 · FIX 1 — pull package name from either `service_line_name`
  // (populated once a contract is signed) or the raw `tier` field. Both
  // are resolved through the shared canonical-name helper so aliases
  // (e.g. legacy "Premium Package") normalise to the current label.
  const rawPackage = property.service_line_name || property.tier || null;
  const packageName = rawPackage ? getCanonicalPackageName(rawPackage) : null;
  const services = useMemo(
    () => (packageName ? getFlattenedServices(packageName) : []),
    [packageName],
  );
  const frequencies = property.service_frequencies || {};
  const freqEntries = Object.entries(frequencies);

  const displayName = property.property_name || `Property ${property.property_id?.slice(-6) || ''}`;
  const region = property.state || property.region || '—';
  const city = property.city && String(property.city).trim() ? property.city : '';

  return (
    <li
      className="rounded-lg border border-zinc-800 bg-zinc-950/70"
      data-testid={testId}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-2 p-3 text-left min-h-[48px]"
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-white truncate">{displayName}</p>
          <p className="text-[10px] text-zinc-500 mt-0.5 flex items-center gap-1">
            <span className="uppercase tracking-widest">{commercialLabel}</span>
            <span>·</span>
            <MapPin className="h-3 w-3" /> {region}{city ? ` · ${city}` : ' · city pending'}
          </p>
        </div>
        <Badge className="bg-cyan-500/15 text-cyan-200 border border-cyan-500/40 text-[9px]">
          {packageName || 'No package assigned'}
        </Badge>
      </button>
      {expanded && (
        <div className="border-t border-zinc-800 p-3 space-y-2" data-testid={`${testId}-expanded`}>
          {packageName ? (
            <>
              <p className="text-[9px] uppercase tracking-widest text-zinc-500">Included services</p>
              <ul className="grid grid-cols-1 gap-1" data-testid={`${testId}-services`}>
                {services.map((svc, i) => (
                  <li key={i} className="text-[11px] text-zinc-200 pl-2 border-l-2 border-cyan-500/40">
                    {svc}
                  </li>
                ))}
              </ul>
              {freqEntries.length > 0 && (
                <>
                  <p className="text-[9px] uppercase tracking-widest text-zinc-500 mt-2">Service frequencies</p>
                  <ul className="grid grid-cols-2 gap-1" data-testid={`${testId}-frequencies`}>
                    {freqEntries.map(([k, v]) => (
                      <li key={k} className="text-[11px] text-zinc-300">
                        <span className="text-zinc-500">{k.replace(/_/g, ' ')}:</span>{' '}
                        <span className="font-mono text-zinc-100">{String(v)}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          ) : (
            <p className="text-[11px] text-zinc-500">
              No service package assigned yet. Field management can add one from the property record.
            </p>
          )}
        </div>
      )}
    </li>
  );
}

function StatTile({ label, value, accent, testId }) {
  return (
    <div className="rounded bg-zinc-900 border border-zinc-800 p-2" data-testid={testId}>
      <p className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</p>
      <p className="text-base font-black" style={{ color: accent }}>{value}</p>
    </div>
  );
}

function BoardCard({ testId, title, icon, children }) {
  return (
    <div className="rounded-lg bg-zinc-950/70 border border-zinc-800 p-3" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-300">{title}</p>
      </div>
      {children}
    </div>
  );
}
